<template>
  <div class="stripe-external-account-form-page">
    <div
      v-if="model"
      class="app-content-page app-content-page--transparent stripe-external-account-form-page__header"
    >
      <app-back-button
        v-if="enableBackButton"
        class="stripe-external-account-form-page__header__back"
        :i18n-code="routeBack.i18nCode"
        :route-name="routeBack.name"
        :route-params="routeBack.params"
      />

      <h1 class="app-content-title stripe-external-account-form-page__header__title">
        <app-i18n code="entities.stripeExternalAccount.form.title.bankAccount" />
      </h1>
    </div>

    <div class="app-content-page">
      <el-form
        v-if="model"
        :model="model"
        :rules="rules"
        class="form"
        ref="form"
        :validate-on-rule-change="false"
        v-loading="isLoading"
        @submit.native.prevent="doSubmit"
        @validate="onFieldValidation"
      >
        <h2 class="app-content-subtitle">
          <app-i18n code="entities.stripeExternalAccount.form.subtitle.information" />
        </h2>

        <el-row :gutter="16">
          <el-col :lg="12" :md="12" :sm="24">
            <el-form-item
              :label="fields.holderName.label"
              :prop="fields.holderName.name"
              :error="getFieldError(fields.holderName.name).message"
              required
            >
              <el-input
                v-model="model[fields.holderName.name]"
                :placeholder="i18n(`entities.stripeExternalAccount.placeholders.${fields.holderName.name}`)"
                prefix-icon="icon-player"
              />
            </el-form-item>
          </el-col>

          <el-col :lg="12" :md="12" :sm="24">
            <el-form-item
              :label="fields.holderType.label"
              :prop="fields.holderType.name"
              :error="getFieldError(fields.holderType.name).message"
              required
            >
              <el-select
                :placeholder="i18n(`entities.stripeExternalAccount.placeholders.${fields.holderType.name}`)"
                v-model="model[fields.holderType.name]"
              >
                <i
                  slot="prefix"
                  class="icon-home"
                />
                <el-option
                  :key="option.id"
                  :label="option.label"
                  :value="option.id"
                  v-for="option in fields.holderType.options"
                />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :lg="12" :md="12" :sm="24">
            <el-form-item
              :label="fields.currency.label"
              :prop="fields.currency.name"
            >
              <app-currency-select
                v-model="model[fields.currency.name]"
                :options="supportedCurrencies"
                :country="model[fields.country.name]"
                :placeholder="i18n(`entities.stripeConnectedAccount.placeholders.${fields.currency.name}`)"
                disabled
              />
            </el-form-item>
          </el-col>

          <el-col :lg="12" :md="12" :sm="24">
            <el-form-item
              :label="fields.country.label"
              :prop="fields.country.name"
              :required="fields.country.required"
            >
              <el-select
                filterable
                v-model="model[fields.country.name]"
                :disabled="availableExternalAccountCountries.length === 1"
                @change="onCountryChange"
              >
                <i slot="prefix" class="icon-location" />
                <el-option
                  :key="option.id"
                  :label="option.label"
                  :value="option.id"
                  v-for="option in availableExternalAccountCountries"
                />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col
            v-for="{ labelKey } in availableBankAccountFields"
            :key="`stripe-external-account-form-page--${labelKey}`"
            :lg="24" :md="24" :sm="24"
          >
            <app-account-number-input
              v-if="isAccountFieldEnabled(fields[labelKey].name)"
              v-model="model[fields[labelKey].name]"
              :name="labelKey"
              :field="fields[labelKey]"
              :error="getFieldError(fields[labelKey].name).message"
              :length="fieldsConfig[labelKey].length"
              :mask="fieldsConfig[labelKey].mask"
              :country="model.country"
              prefix-icon="icon-album"
              required
            />
          </el-col>

          <el-col :lg="24" :md="24" :sm="24">
            <el-form-item :prop="fields.defaultForCurrency.name">
              <el-checkbox
                v-model="model[fields.defaultForCurrency.name]"
                :disabled="record.isFirstMethod"
              >
                <app-i18n code="entities.stripeExternalAccount.fields.defaultForCurrency" />
              </el-checkbox>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- Global error message -->
        <el-form-item
          v-if="globalErrorMessage"
          :error="globalErrorMessage"
        />

        <!-- Form controls -->
        <el-form-item>
          <div class="form-buttons form-buttons--sides">
            <!-- Back button -->
            <el-button
              :disabled="saveLoading"
              @click="doBack"
              type="secondary"
            >
              <app-i18n code="common.backButton" />
            </el-button>

            <!-- Create External Account button -->
            <el-button
              :disabled="saveLoading"
              @click="doSubmit"
              type="primary"
            >
              <app-i18n code="entities.stripeExternalAccount.create.title" />
            </el-button>
          </div>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { isString } from 'lodash';

import StripeFrontend from 'shared-stripe-services/dist/Frontend';

import config from '@/config';

import { i18n } from '@shared/i18n';

import { FormSchema } from '@shared/form/form-schema';
import { mapStripeFieldErrors, getStripeFieldNames } from '@shared/fields/helpers/mapStripeFields';

import supportedSettlementCurrencies from '@enums/supportedSettlementCurrencies.json';
import bankAccountFormatsByCountry from '@enums/bankAccountFormatsByCountry.json';

import { StripeExternalAccountModel } from '@modules/stripe/stripe-external-account/stripe-external-account-model';

const stripeFrontend = new StripeFrontend({
  publishableKey: config.stripeConfig.publishableKey,
  fileUploadEndpoint: config.stripeConfig.fileUploadEndpoint,
});

export default {
  name: 'app-stripe-external-account-form-page',
  props: {
    connectedAccountId: String,
  },
  data() {
    return {
      model: {},
      tokenLoading: false,
      activeStepIndex: 0,
      i18n,
      prevRoute: null,
    };
  },
  computed: {
    ...mapGetters({
      record: 'stripeExternalAccount/form/record',
      fieldErrors: 'stripeExternalAccount/form/fieldErrors',
      globalErrorMessage: 'stripeExternalAccount/form/globalErrorMessage',
      findLoading: 'stripeExternalAccount/form/findLoading',
      saveLoading: 'stripeExternalAccount/form/saveLoading',
      newLoading: 'stripeExternalAccount/form/newLoading',
      currentManage: 'auth/manage/currentManage',
      isRootScreen: 'layout/isRootScreen',
    }),

    isLoading() {
      return this.findLoading || this.saveLoading || this.tokenLoading || this.newLoading;
    },

    fields() {
      return StripeExternalAccountModel.fields(this.fieldsConfig)
    },

    fieldsConfig() {
      const { availableBankAccountFields } = this

      if (!availableBankAccountFields || !availableBankAccountFields.length) return {}

      return availableBankAccountFields.reduce((result, field) => {
        const key = field.labelKey
        const { length, mask, type } = field
        const numeric = type === 'NUMERIC'
        result[key] = { length, mask, numeric }
        return result
      }, {})
    },

    formSchema() {
      const fields = [
        this.fields.id,
        this.fields.country,
        this.fields.currency,
        this.fields.holderName,
        this.fields.holderType,
        this.fields.defaultForCurrency,
      ]

      const { availableBankAccountFields } = this
      if (!availableBankAccountFields || !availableBankAccountFields.length) return new FormSchema(fields)

      for (const field of availableBankAccountFields) fields.push(this.fields[field.labelKey])

      return new FormSchema(fields)
    },

    rules() {
      return this.formSchema.rules();
    },

    stripeFieldsMap() {
      return {
        'bank_account[routing_number]': {
          fields: [
            this.fields.routingNumber.name,
            this.fields.transitNumber.name,
            this.fields.institutionNumber.name,
            this.fields.sortCode.name,
          ],
          stepIndex: 0,
        },
        'bank_account[account_number]': {
          fields: [
            this.fields.accountNumber.name,
            this.fields.ibanNumber.name,
          ],
          stepIndex: 0,
        },
      };
    },

    mappedFieldErrors() {
      try {
        const { isLoading, fieldErrors, stripeFieldsMap } = this;
        if (isLoading) return {};
        return mapStripeFieldErrors({
          fieldErrors,
          stripeFieldsMap,
          modelName: 'StripeExternalAccount',
          model: this.model,
        });
      } catch (e) {
        const message = i18n('common.unknownError');
        this.doSetStripeError({ message });
        return {};
      }
    },

    enableBackButton() {
      return isString(this.routeBack.i18nCode) && this.routeBack.name;
    },

    routeBack() {
      if (this.isRootScreen || (this.prevRoute && this.prevRoute.name === 'finantialSettings')) {
        return  {
          name: 'finantialSettings',
          params: {
            propertyId: this.currentManage.property.id,
            subpropertyId: this.currentManage.subproperty.id,
            tabType: 'payoutMethods',
          },
          i18nCode: 'subpropertySettings.menu',
        }
      } else {
        return  {
          name: 'home',
          params: null,
          i18nCode: 'home.nav.dashboard',
        }
      }
    },

    availableBankAccountFields() {
      const { country } = this.model
      if (!country) return []
      return bankAccountFormatsByCountry[country]
    },

    availableExternalAccountCountries() {
      const { connectedAccountCountry: country, currency } = this.record
      if (!country || !currency) return []

      const availableCountries = supportedSettlementCurrencies[country][currency]
      return this.fields.country.options.filter(c => availableCountries.includes(c.id)).sort((a,b) => {
        var valA = a.label.toUpperCase()
        var valB = b.label.toUpperCase()
        return (valA < valB) ? -1 : (valA > valB) ? 1 : 0
      })
    },

    supportedCurrencies() {
      const { model, fields } = this
      const currencyCode = model[fields.currency.name]
      const supportedCurrency = fields.currency.options.find(c => c.id === currencyCode)
      return [supportedCurrency]
    },
  },

  async mounted() {
    await this.doNew({
      connectedAccountId: this.connectedAccountId,
      propertyId: this.currentManage.property.id,
    });
    this.model = this.formSchema.initialValues(this.record);
    await this.$nextTick();
    this.onCountryChange();
  },

  beforeRouteEnter(to, from, next) {
    next(vm => vm.prevRoute = from)
  },

  methods: {
    ...mapActions({
      doNew: 'stripeExternalAccount/form/doNew',
      doCreate: 'stripeExternalAccount/form/doCreate',
      doHandleStripeError: 'stripeExternalAccount/form/doHandleStripeError',
      doSetStripeError: 'stripeExternalAccount/form/doSetStripeError',
      doClearStripeError: 'stripeExternalAccount/form/doClearStripeError',
    }),

    async onCountryChange() {
      const clearFields = [
        'sortCode',
        'transitNumber',
        'institutionNumber',
        'routingNumber',
        'accountNumber',
        'ibanNumber',
      ]

      for (const field of clearFields) {
        if (!this.model[field]) continue
        this.model[field] = undefined
      }

      await this.$nextTick()
      this.$refs.form.clearValidate()
    },

    doBack() {
      const { name, params } = this.routeBack
        const route = { name }
        if (params) route.params = params
        this.$router.push(route)
    },

    async doValidation() {
      try {
        const { form, addressForm } = this.$refs;
        const forms = [];

        if (form) forms.push(form);
        if (addressForm) forms.push(addressForm.$refs.form);

        await Promise.all(forms.map(form => {
          form.clearValidate();
          return form.validate();
        }));
      } catch (error) {
        throw error;
      }
    },

    async doSubmit() {
      try {
        await this.doValidation();
      } catch (error) {
        return;
      }

      try {
        this.tokenLoading = true;

        const data = this.formSchema.cast(this.model);
        delete data.id;

        const tokenResult = await this.generateStripeExternalAccountToken(data);
        if ('error' in tokenResult) throw tokenResult.error;

        const { token: { id: externalAccountToken } } = tokenResult;

        const backToSettings = this.prevRoute && this.prevRoute.name === 'finantialSettings'

        return this.doCreate({
          externalAccountToken,
          defaultForCurrency: data.defaultForCurrency,
          propertyId: this.currentManage.property.id,
          subpropertyId: this.currentManage.subproperty.id,
          backToSettings,
        });
      } catch (error) {
        this.doHandleStripeError(error);
      } finally {
        this.tokenLoading = false;
      }
    },

    isAccountFieldEnabled(name) {
      return this.fieldsConfig && name in this.fieldsConfig
    },

    parseAccountNumber(data) {
      if (data.ibanNumber) return data.ibanNumber
      if (data.accountNumber) return data.accountNumber
      return null
    },

    parseRoutingNumber(data) {
      if (data.transitNumber && data.institutionNumber) return `${data.transitNumber}${data.institutionNumber}`
      if (data.sortCode) return data.sortCode
      if (data.routingNumber) return data.routingNumber
      return null
    },

    async generateStripeExternalAccountToken(data) {
      const parsedAccountNumber = this.parseAccountNumber(data)
      const parsedRoutingNumber = this.parseRoutingNumber(data)

      const accountNumber = parsedAccountNumber && parsedAccountNumber.replace(/ |-/g, '')
      const routingNumber = parsedRoutingNumber && parsedRoutingNumber.replace(/ |-/g, '')

      return stripeFrontend.createBankAccountToken({
        country: data.country,
        currency: data.currency,
        holderName: data.holderName,
        holderType: data.holderType,
        accountNumber,
        routingNumber,
      });
    },

    getFieldError(fieldName) {
      const mappedError = this.mappedFieldErrors[fieldName];
      if (!mappedError) return {};
      return mappedError;
    },

    findKeyRecursively(object, key) {
      let value;

      Object.keys(object).some(k => {
        if (k === key) {
          value = object[k];
          return true;
        }
        if (object[k] && typeof object[k] === 'object') {
          value = this.findKeyRecursively(object[k], key);
          return value !== undefined;
        }
      });

      return value;
    },

    onFieldValidation(formFieldName) {
      if (!this.globalErrorMessage) return;

      const { stripeFieldsMap } = this;
      const fieldNames = getStripeFieldNames({
        fieldName: formFieldName,
        stripeFieldsMap,
      });

      if (!fieldNames || !fieldNames.length) return;

      this.doClearStripeError({ fieldNames });
    },
  },
  watch: {
    mappedFieldErrors: {
      handler(val) {
        if (!Object.keys(val).length) return;
        this.activeStepIndex = this.findKeyRecursively(val, 'stepIndex');
      },
      deep: true
    }
  }
};
</script>

<style lang="scss">
.stripe-external-account-form-page {
  &__header {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: 16px auto;
    grid-template-areas:
      'back .'
      'title radio';
    grid-row-gap: 24px;
    align-items: center;
    margin-left: 8px;

    &__back {
      grid-area: back;
    }

    &__radio {
      grid-area: radio;
    }

    &__title {
      grid-area: title;
      margin: 0;
    }
  }

  &__form-item {
    body .el-form &--ssn,
    body .el-form &--ownership {
      .el-form-item__error {
        white-space: nowrap;
      }
    }

    body .el-form &--checkbox {
      margin-bottom: 8px;

      .el-checkbox__label {
        display: inline-flex;
        align-items: center;
        margin-left: 8px;
        font-size: 14px;
        font-weight: normal;
        font-style: normal;
        font-stretch: normal;
        line-height: 1.29;
        letter-spacing: -0.3px;
        color: #333333;
      }

      .el-checkbox__inner {
        width: 20px;
        height: 20px;

        &::after {
          width: 4px;
          height: 12px;

          left: 6px;
          top: 0;
        }
      }
    }
  }

  .el-tooltip {
    margin-left: 16px;

    &::before {
      font-size: 20px;
      color: #cccccc;
    }
  }

  &__message {
    margin: 0;
    color: #404040;
  }

  &__tooltip {
    max-width: 240px;
    padding: 8px 8px 8px 16px;
  }

  .form-buttons {
    margin-top: 24px;
  }
}
</style>
